import React, { memo, useMemo } from "react";

import { StateCallbacks } from "../../state";
import {
  ContentType,
  ImageBlock,
  MathBlock,
  Page as PageType,
  State,
  TableBlock,
  TableCellBlock,
  TextBlock,
} from "../../types";
import { ImageZone } from "./ImageZone";
import { NullCellZone } from "./NullCellZone";
import { TableCellZone } from "./TableCellZone";
import { TableZone } from "./TableZone";
import { TextZone } from "./TextZone";

type Props = Readonly<{
  activeBlockId: string | null;
  blocks: State["blocks"];
  containerWidth: number;
  confirmTableSelection: boolean;
  disableTableSelectionConfirmation: () => void;
  onSelectBlock: StateCallbacks["onSelectBlock"];
  onAddCorrections: StateCallbacks["onAddCorrections"];
  setShowReferences: (blockId: string) => void;
  page: Required<PageType>;
  zoom: number;
}>;

const GUTTER = 20;

const PageComponent = ({
  activeBlockId,
  blocks,
  confirmTableSelection,
  containerWidth,
  disableTableSelectionConfirmation,
  onSelectBlock,
  onAddCorrections,
  page,
  setShowReferences,
  zoom,
}: Props) => {
  const scale = useMemo(() => ((containerWidth - 2 * GUTTER) / page.width) * zoom, [
    containerWidth,
    page.width,
    zoom,
  ]);
  const pageDimensions = useMemo(
    () => ({
      height: `${page.height * scale}px`,
      width: `${page.width * scale + 2 * GUTTER}px`,
    }),
    [page.height, page.width, scale],
  );
  const relevantBlocks = useMemo(
    () =>
      page.blocks.reduce((prev: Array<TextBlock | ImageBlock | MathBlock | TableBlock>, next) => {
        if (next in blocks) {
          const block = blocks[next];
          switch (block.type) {
            case ContentType.Image:
            case ContentType.Math:
            case ContentType.Table:
            case ContentType.Text:
              prev.push(block);
              break;
          }
        }
        return prev;
      }, []),
    [blocks, page.blocks],
  );

  const contents = relevantBlocks.map(block => {
    const selected = block.id === activeBlockId;
    switch (block.type) {
      case ContentType.Table: {
        return (
          <TableZone
            block={block}
            confirmTableSelection={confirmTableSelection}
            key={block.id}
            onDisableTableSelectionConfirmation={disableTableSelectionConfirmation}
            gutter={GUTTER}
            onShowReferences={setShowReferences}
            onSelectBlock={onSelectBlock}
            scale={scale}
            selected={selected}
          >
            {block.cells.map(r =>
              r.map(c => {
                const cell = blocks[c] as TableCellBlock;
                if (isNaN(cell.height) || isNaN(cell.width) || isNaN(cell.x) || isNaN(cell.y)) {
                  return (
                    <NullCellZone id={cell.id} key={cell.id} selected={cell.id === activeBlockId} />
                  );
                }
                return (
                  <TableCellZone
                    cell={cell}
                    gutter={GUTTER}
                    key={cell.id}
                    onSelectCell={onSelectBlock}
                    onShowReferences={setShowReferences}
                    scale={scale}
                    selected={cell.id === activeBlockId}
                  />
                );
              }),
            )}
          </TableZone>
        );
      }
      case ContentType.Text: {
        return (
          <TextZone
            block={block}
            gutter={GUTTER}
            key={block.id}
            onShowReferences={setShowReferences}
            onAddCorrections={onAddCorrections}
            onSelectBlock={onSelectBlock}
            scale={scale}
            selected={selected}
          />
        );
      }
      case ContentType.Image:
      case ContentType.Math: {
        return (
          <ImageZone
            block={block}
            gutter={GUTTER}
            key={block.id}
            onShowReferences={setShowReferences}
            onSelectBlock={onSelectBlock}
            scale={scale}
            selected={selected}
          />
        );
      }
    }
  });

  return (
    <div className="page" style={pageDimensions}>
      <img className="image" src={page.src} />
      <div className="zones">{contents}</div>
    </div>
  );
};
PageComponent.displayName = "Page";
export const Page = memo(PageComponent);
